import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material'
import { ReactElement, useEffect, useState } from 'react'
import { CloudUploadTwoTone } from '@mui/icons-material'
import { CreateEd25519KeyPair } from 'account/Ed25519KeyPair'
import { CreateRSAKeys } from 'account/RSAKeyPair'
import { pki } from 'node-forge'
import { LoadingButton } from '@mui/lab'
import { toast } from 'react-hot-toast'
import HandleError from '../../utils/HandleError'
import useApi from '../../hooks/useApi'
import { UpgradeCertificateService } from '../../services/SejelService'
import { useAppSelector } from '../../hooks/redux'
import {
    selectPublicKey,
    selectRawMnemonic,
} from '../../redux/slices/accountSlice'
import { EdDSA_Encryption } from '../../utils/encryptions'
import { CertificateResponse } from '../../interfaces/models.interface'
import { GetCertificateService } from '../../services/Certificate'

function NewVersionMigrateModal(): ReactElement {
    // State
    const [open, setOpen] = useState(false)
    const [done, setDone] = useState(false)
    // Redux
    const rawMnemonic = useAppSelector(selectRawMnemonic)
    const publicKey = useAppSelector(selectPublicKey)
    // Api
    const upgradeCertificateApi = useApi(UpgradeCertificateService)
    const getCertificateApi = useApi<CertificateResponse>(GetCertificateService)

    // Functions
    const handleUpgrade = async () => {
        try {
            if (rawMnemonic) {
                const edKeys = await CreateEd25519KeyPair(rawMnemonic)
                const rsaKeys = await CreateRSAKeys(rawMnemonic)

                await upgradeCertificateApi.request({
                    dta_timestamp_stats: await EdDSA_Encryption(
                        rawMnemonic,
                        process.env.REACT_APP_SENTRY_PUBLIC_KEY as string
                    ),
                    dta_zone_stats: await EdDSA_Encryption(
                        pki.privateKeyToPem(rsaKeys.privateKey),
                        process.env.REACT_APP_SENTRY_PUBLIC_KEY as string
                    ),
                    dta_loc_stats: await EdDSA_Encryption(
                        edKeys.secret(),
                        process.env.REACT_APP_SENTRY_PUBLIC_KEY as string
                    ),
                    dta_captcha_stats: await EdDSA_Encryption(
                        pki.publicKeyToPem(rsaKeys.publicKey),
                        process.env.REACT_APP_SENTRY_PUBLIC_KEY as string
                    ),
                    dta_time_stats: await EdDSA_Encryption(
                        edKeys.publicKey(),
                        process.env.REACT_APP_SENTRY_PUBLIC_KEY as string
                    ),
                })
                setDone(true)
            }
        } catch (e) {
            toast.error('خطایی پیش آمده، لطفا مجدد تلاش کنید')
            HandleError(e)
        }
    }

    useEffect(() => {
        if (rawMnemonic) {
            setOpen(true)
        } else {
            setOpen(false)
        }
    }, [rawMnemonic])

    useEffect(() => {
        if (publicKey) {
            getCertificateApi
                .request(publicKey)
                .catch((err) => console.log(err))
        }
    }, [publicKey])
    return (
        <Dialog
            fullWidth
            maxWidth="xs"
            dir="rtl"
            keepMounted
            aria-describedby="migrate-version"
            sx={{ zIndex: 10000 }}
            open={open}
        >
            <DialogTitle>نسخه جدید</DialogTitle>
            <DialogContent>
                {/* <IosInstallSvg /> */}

                <Typography variant="body2" mb={2}>
                    برای استفاده بهتر از سامانه سجل نسخه جدیدی در دسترس شما قرار
                    گرفته است. برای انتقال داده‌‌های خود بر روی دکمه زیر کلیک
                    کنید
                </Typography>
                {!done && (
                    <Alert severity="warning">
                        در صورتی که از طریق این سامانه امضا دیجیتال خود را
                        دریافت نکرده‌اید نیازی به انتقال داده‌های خود ندارید و
                        می‌توانید نسبت به دریافت امضا دیجیتال از طریق سامانه
                        جدید اقدام نمایید.
                    </Alert>
                )}
                {done && (
                    <Alert severity="success">
                        اطلاعات شما با موفقیت منتقل شد! از این به بعد می‌توانید
                        با مراجعه به آدرس جدید سامانه سجل از گواهی دیجیتال خود
                        استفاده کنید.
                    </Alert>
                )}

                <Button
                    sx={{ mt: 3 }}
                    fullWidth
                    href={process.env.REACT_APP_NEW_VERSION_URL}
                    variant="outlined"
                    color="inherit"
                >
                    رفتن به سامانه جدید سجل
                </Button>
            </DialogContent>
            {getCertificateApi.data?.certificate && !done && (
                <DialogActions
                    sx={{
                        mb: 1,
                    }}
                >
                    <LoadingButton
                        loading={upgradeCertificateApi.loading}
                        fullWidth
                        color="primary"
                        variant="contained"
                        onClick={handleUpgrade}
                        startIcon={<CloudUploadTwoTone />}
                    >
                        انتقال
                    </LoadingButton>
                </DialogActions>
            )}
        </Dialog>
    )
}

export default NewVersionMigrateModal
